import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { deFondo } from "src/app/security/helper/peticion-de-fondo";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";

/** Sobre que devuelven los endpoints de Azure DevOps (AzureDevOpsService.java). */
export interface RespuestaAzure<T> {
  data: T;
  codRet: string;
  mensaje?: string;
}

/** Proyecto de Azure DevOps tal como lo lista `controller/proyectos.do`. */
export interface ProyectoAzure {
  id: string;
  nombre: string;
  estado?: string;
  descripcion?: string;
  /** Solo llega cuando se pide con `conTareas=true`. */
  tareas?: TareaAzure[];
}

/** Work item de Azure DevOps; el backend siempre manda al menos el titulo. */
export interface TareaAzure {
  titulo: string;
  [campo: string]: unknown;
}

/**
 * Consultas a Azure DevOps que pasan por el backend del COS.
 *
 * Las URLs de UrlConstantes ya traen el prefijo "controller/" (apiUrl es ''),
 * asi que se usan tal cual. El servicio anterior (tarea-alta/azure.service.ts)
 * le volvia a anteponer "controller/" y la peticion terminaba en
 * "controller/controller/proyectos.do", que no existe.
 */
@Injectable({ providedIn: "root" })
export class AzureService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Proyectos de Azure DevOps que ve el token del usuario. Sin `conTareas`
   * el backend no consulta los work items de cada proyecto (una llamada a
   * Azure por proyecto), que es lo que se quiere para llenar un selector.
   */
  getProyectosAzure(conTareas = false): Observable<RespuestaAzure<ProyectoAzure[]>> {
    const params = new HttpParams().set("conTareas", String(conTareas));
    return this.http.get<RespuestaAzure<ProyectoAzure[]>>(
      UrlConstantes.PROYECTOS_Consulta_Azure,
      { params }
    );
  }

  /**
   * Work items de un proyecto de Azure DevOps (por id o por nombre).
   *
   * Va como peticion de fondo: solo alimenta las sugerencias de "Nombre de
   * tarea" mientras el usuario escribe, asi que no debe tapar la pantalla
   * con el velo de "Cargando…" ni cerrar la sesion si falla; quien la llama
   * muestra un aviso y deja seguir capturando.
   */
  getTareasProyectoAzure(idONombre: string): Observable<RespuestaAzure<TareaAzure[]>> {
    const params = new HttpParams().set("proyecto", idONombre);
    return this.http.get<RespuestaAzure<TareaAzure[]>>(
      UrlConstantes.PROYECTOS_AZURE_TAREAS,
      { params, context: deFondo() }
    );
  }
}
